import React, { useMemo } from 'react';
import { times, range } from 'lodash-es';
import HBLoadingSkeleton from '../../HBLoadingSkeleton/HBLoadingSkeleton';
import {
    SkeletonBoard,
    SkeletonColumn,
    SkeletonColumnHeader,
    SkeletonCard,
    SkeletonColumnContent,
    SkeletonColumnItems
} from './KanbanSkeletonLoader.styles';
import {
    KanbanColumn,
    KanbanRegularColumn,
    KanbanSkeletonProps
} from '../HBKanban.types';
import HBText from '../../HBText/HBText';
import {
    StyledColumnTitle,
    StyledItemCount
} from '../HBKanbanColumn/ColumnHeader/ColumnHeader.styles';
import HBKanbanDivider from '../HBKanbanColumn/HBKanbanDivider';

interface KanbanSkeletonLoaderProps<TColumnData = any> {
    columns?: KanbanColumn<TColumnData>[];
    skeletonProps?: KanbanSkeletonProps;
    renderColumnHeaderTitle?: (
        column: KanbanColumn<TColumnData>
    ) => React.ReactNode;
}

// Constants for deterministic skeleton generation
const MIN_CARDS_PER_COLUMN = 2;
const MAX_CARDS_PER_COLUMN = 8;
const MIN_SKELETON_WIDTH_PERCENT = 60;
const MAX_SKELETON_WIDTH_PERCENT = 100;
const HASH_MODULO = 100; // Used to normalize hash values to 0-99 range

const KanbanSkeletonLoader = <TColumnData = any,>({
    columns,
    skeletonProps,
    renderColumnHeaderTitle
}: KanbanSkeletonLoaderProps<TColumnData>) => {
    // Convert skeletonProps to a structure we can use for rendering
    const skeletonStructure = useMemo(() => {
        if (!skeletonProps) {
            return undefined;
        }

        return skeletonProps.columns.map((skeletonItem, index) => {
            if (skeletonItem.type === 'divider') {
                return {
                    type: 'divider' as const,
                    id: `divider-${index}`,
                    color: skeletonItem.color
                };
            }

            return {
                id: `skeleton-column-${index}`,
                type: 'column' as const,
                title: skeletonItem.title || '',
                intent: skeletonItem.intent,
                data: {}
            };
        });
    }, [skeletonProps]);

    // Check if we have real columns (not skeleton props)
    const hasRealColumns = columns && columns.length > 0;
    // Check if we have skeleton props structure
    const hasSkeletonStructure =
        skeletonStructure && skeletonStructure.length > 0;

    // Use real columns if provided, otherwise use skeleton structure
    const columnsToRender: KanbanColumn<TColumnData>[] = hasRealColumns
        ? columns
        : (skeletonStructure as KanbanColumn<TColumnData>[]) || [];
    const hasColumns = hasRealColumns || hasSkeletonStructure;

    // Filter out dividers to calculate card counts only for regular columns
    const regularColumns = hasColumns
        ? columnsToRender.filter(
              (col): col is KanbanRegularColumn<TColumnData> =>
                  col.type !== 'divider'
          )
        : [];
    const numRegularColumns = hasColumns ? regularColumns.length : 4;

    // Generate consistent values based on column and card indices
    // This ensures the same column position always gets the same skeleton layout
    // regardless of whether it's in loading state or final state
    const getHashValue = (columnIndex: number, cardIndex: number): number => {
        // Combine indices to create unique values for each position
        // Multiply by different numbers to ensure columns produce different patterns
        const combined = columnIndex * 37 + cardIndex * 13;
        return combined % HASH_MODULO; // Returns 0-99
    };

    // Generate consistent card counts based on regular column index
    // Each column gets a deterministic number of cards based on its position
    const columnCardCounts = useMemo(() => {
        const cardCountRange = MAX_CARDS_PER_COLUMN - MIN_CARDS_PER_COLUMN + 1;
        return times(numRegularColumns, index => {
            const hash = getHashValue(index, 0);
            return MIN_CARDS_PER_COLUMN + (hash % cardCountRange);
        });
    }, [numRegularColumns]);

    // Generate consistent skeleton widths for each card
    // Each card has 3 lines, so we generate 3 different widths per card
    const getSkeletonWidths = useMemo(() => {
        const widthRange =
            MAX_SKELETON_WIDTH_PERCENT - MIN_SKELETON_WIDTH_PERCENT + 1;
        return columnCardCounts.map((cardCount, columnIndex) =>
            times(cardCount, cardIndex =>
                range(3).map(lineIndex => {
                    const hash = getHashValue(
                        columnIndex,
                        cardIndex * 3 + lineIndex
                    );
                    return MIN_SKELETON_WIDTH_PERCENT + (hash % widthRange);
                })
            )
        );
    }, [columnCardCounts]);

    return (
        <SkeletonBoard>
            {hasColumns
                ? // Render column headers (real or skeleton based on hasRealColumns)
                  columnsToRender.map((column, columnIndex) => {
                      // Render dividers in skeleton loader to prevent layout shifts
                      if (column.type === 'divider') {
                          return (
                              <HBKanbanDivider
                                  key={`divider-${columnIndex}`}
                                  column={column}
                              />
                          );
                      }

                      // Calculate the regular column index by counting non-dividers before this column
                      const regularColumnIndex = columnsToRender
                          .slice(0, columnIndex)
                          .filter(
                              (col): col is KanbanRegularColumn<TColumnData> =>
                                  col.type !== 'divider'
                          ).length;

                      return (
                          <SkeletonColumn key={column.id}>
                              <SkeletonColumnHeader>
                                  {hasRealColumns ? (
                                      // Render actual column headers when real columns are provided
                                      <StyledColumnTitle>
                                          {renderColumnHeaderTitle ? (
                                              renderColumnHeaderTitle(column)
                                          ) : (
                                              <HBText
                                                  type="emphasis18"
                                                  color="text.primary"
                                              >
                                                  {column.title}
                                              </HBText>
                                          )}
                                          {column.count !== undefined && (
                                              <StyledItemCount>
                                                  <HBText
                                                      type="body14"
                                                      color="text.secondary"
                                                  >
                                                      {column.count}
                                                  </HBText>
                                              </StyledItemCount>
                                          )}
                                      </StyledColumnTitle>
                                  ) : (
                                      // Render skeleton headers when using skeletonProps
                                      <HBLoadingSkeleton
                                          type="text"
                                          lines={[
                                              { width: '80px', height: 21 }
                                          ]}
                                          style={{
                                              lineHeight: '33px'
                                          }}
                                      />
                                  )}
                              </SkeletonColumnHeader>
                              <SkeletonColumnContent $intent={column.intent}>
                                  <SkeletonColumnItems>
                                      {times(
                                          columnCardCounts[regularColumnIndex],
                                          cardIndex => {
                                              const widths =
                                                  getSkeletonWidths[
                                                      regularColumnIndex
                                                  ][cardIndex];
                                              return (
                                                  <SkeletonCard key={cardIndex}>
                                                      <HBLoadingSkeleton
                                                          type="text"
                                                          lines={widths.map(
                                                              width => ({
                                                                  width: `${width}%`,
                                                                  height: 16
                                                              })
                                                          )}
                                                      />
                                                  </SkeletonCard>
                                              );
                                          }
                                      )}
                                  </SkeletonColumnItems>
                              </SkeletonColumnContent>
                          </SkeletonColumn>
                      );
                  })
                : // Render skeleton headers when no columns provided
                  columnCardCounts.map((cardCount, columnIndex) => (
                      <SkeletonColumn key={columnIndex}>
                          <SkeletonColumnHeader>
                              <HBLoadingSkeleton
                                  type="text"
                                  lines={[{ width: '80px', height: 21 }]}
                                  style={{
                                      lineHeight: '33px'
                                  }}
                              />
                          </SkeletonColumnHeader>
                          <SkeletonColumnContent>
                              <SkeletonColumnItems>
                                  {times(cardCount, cardIndex => {
                                      const widths =
                                          getSkeletonWidths[columnIndex][
                                              cardIndex
                                          ];
                                      return (
                                          <SkeletonCard key={cardIndex}>
                                              <HBLoadingSkeleton
                                                  type="text"
                                                  lines={widths.map(width => ({
                                                      width: `${width}%`,
                                                      height: 16
                                                  }))}
                                              />
                                          </SkeletonCard>
                                      );
                                  })}
                              </SkeletonColumnItems>
                          </SkeletonColumnContent>
                      </SkeletonColumn>
                  ))}
        </SkeletonBoard>
    );
};

export default KanbanSkeletonLoader;
